import { ReactNode, useEffect } from 'react'

import { Helmet } from 'react-helmet'
import { useLocation } from 'react-router'

import { useCommandBar } from '@tribeplatform/react-components/CommandBar'
import { useReadSpace } from '@tribeplatform/react-sdk/hooks'
import { Network, Page, SpaceType } from '@tribeplatform/react-sdk/types'
import {
  SlateRenderer,
  SlateRendererProps,
} from '@tribeplatform/react-slate-kit/components'

import { useAnalytics } from '../../hooks/useAnalytics'
import { Layout, SlateLayout } from '../Layout'
import { NewSpaceBanner } from './NewSpaceBanner'
import { GenericSpaceLoading } from './SpaceLoading'

export const Space = ({
  network,
  page,
  context,
}: {
  network: Network
  page: Page
  context: SlateRendererProps['context']
}) => {
  const { space } = page || {}

  const { mutateAsync: readSpace } = useReadSpace()
  const { setContext: setCommandBarContext } = useCommandBar()

  const { pathname } = useLocation()

  const analytics = useAnalytics({
    target: { spaceId: space?.id, spaceType: space?.type },
  })

  const { title: pageTitle = space?.name || space?.slug } =
    space?.seoDetail || {}
  const title = `${pageTitle} - ${network?.name}`

  useEffect(() => {
    if (!space?.id) {
      return
    }

    analytics.trackPageView(title)
    if (
      space?.type === SpaceType.Group &&
      space?.authMemberProps?.unreadPostsCount > 0
    ) {
      readSpace({ spaceId: space?.id, date: new Date().toISOString() })
    }

    if (space?.type === SpaceType.Group || space?.type === SpaceType.Page) {
      setTimeout(() => {
        setCommandBarContext({ entity: space, entityType: 'space' })
      }, 100)
    }

    return () => setCommandBarContext({ entity: null, entityType: 'network' })
  }, [title, space])

  let content: ReactNode = null
  if (!space) {
    content = <GenericSpaceLoading />
  } else {
    content = (
      <>
        <Helmet>
          {space.type === SpaceType.Group && pathname !== '/' && (
            <link
              rel="alternate"
              type="application/rss+xml"
              title={space.name}
              href={`https://${network?.domain}/rss/${space.id}/posts`}
            />
          )}
        </Helmet>
        <NewSpaceBanner space={space} />
        <SlateRenderer
          slate={space.slate}
          context={{ ...context, space, spaceId: space.id }}
        />
      </>
    )
  }

  return (
    <Layout
      layout={space?.layout as SlateLayout}
      navigationSlates={network?.navigationSlates}
      context={{ ...context, space, spaceId: space?.id }}
    >
      {content}
    </Layout>
  )
}
